import React, { useState, useEffect } from "react";
import { Col, Container, Form, Row } from "reactstrap";
import s from "./styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";
import { FormProvider, useForm } from "react-hook-form";
import AuthLeftPanel from "../AuthLeftPanel";
import loginWordmarkWhite from "../../../media/img/auth/login-wordmark-white.png";
import FormInput from "../../../toolkit/formInput";
import PrimaryButton from "../../../toolkit/primaryButton";
import PageTransition from "../../../toolkit/pageTransition";
import { emailRules, passwordRules } from "../../../utils/validation/validationRules";
import {
  executeForgotPassword,
  checkForgotPasswordOperation,
  saveForgotPassword,
  adminOtpChallenge,
} from "../../../utils/fetch";
import { serviceCaller, ServiceCallerProps } from "../../../Admin/composition";
import { withRouter, RouterProps } from "../../../utils/withRouter";
import { useParams, Link } from "react-router-dom";
import { CircularProgress } from "@mui/material";
import LoadingSpinner from "../../../toolkit/loadingSpinner";
import { MobileLogo, AuthFormCol, AuthContentCol } from "../shared";
import AdminOtpPanel from "../../../toolkit/adminOtpPanel";

interface ForgotPasswordForm {
  email: string;
}

interface ResetPasswordForm {
  newPassword: string;
  confirmPassword: string;
}

interface ForgotPasswordPageProps extends ServiceCallerProps, RouterProps {}

const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({
  config,
  navigate,
}) => {
  const { operationId } = useParams<{ operationId?: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [isValidLink, setIsValidLink] = useState(true);
  const [checkingLink, setCheckingLink] = useState(!!operationId);
  const [resetFlow, setResetFlow] = useState<"init" | "otp" | "password">(
    "init"
  );
  const [forgotChallengeId, setForgotChallengeId] = useState<string | null>(
    null
  );
  const [forgotOtpSmsEligible, setForgotOtpSmsEligible] = useState(false);
  const [forgotOtpDeliveryChannel, setForgotOtpDeliveryChannel] = useState<
    "email" | "sms"
  >("email");
  const [forgotCompletionToken, setForgotCompletionToken] = useState<
    string | null
  >(null);
  const [otpPrimed, setOtpPrimed] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const forgotMethods = useForm<ForgotPasswordForm>();
  const resetMethods = useForm<ResetPasswordForm>();

  useEffect(() => {
    if (operationId && config?.authServiceUrl) {
      checkForgotPasswordOperation(config.authServiceUrl, operationId)
        .then(response => {
          setIsValidLink(true);
          setCheckingLink(false);
        })
        .catch(() => {
          setIsValidLink(false);
          setCheckingLink(false);
        });
    } else if (!operationId) {
      setCheckingLink(false);
    }
  }, [operationId, config]);

  useEffect(() => {
    if (
      !operationId ||
      !config?.authServiceUrl ||
      !isValidLink ||
      checkingLink ||
      otpPrimed
    ) {
      return;
    }
    let cancelled = false;
    setOtpPrimed(true);
    setIsLoading(true);
    setError(null);
    adminOtpChallenge(config.authServiceUrl, {
      clientType: "web",
      operation: "forgot",
      operationId,
    })
      .then(ch => {
        if (cancelled) {
          return;
        }
        if (!ch.otpRequired && ch.completionToken) {
          setForgotCompletionToken(ch.completionToken);
          setResetFlow("password");
          setIsLoading(false);
          return;
        }
        if (ch.challengeId) {
          setForgotChallengeId(ch.challengeId);
          setForgotOtpSmsEligible(ch.deliveryChannel === "sms");
          setForgotOtpDeliveryChannel(ch.deliveryChannel === "sms" ? "sms" : "email");
          setResetFlow("otp");
          setIsLoading(false);
          return;
        }
        setError("Could not start verification.");
        setIsLoading(false);
      })
      .catch(err => {
        if (!cancelled) {
          setError(err?.message || "Verification failed.");
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [
    operationId,
    config?.authServiceUrl,
    isValidLink,
    checkingLink,
    otpPrimed,
  ]);

  const onForgotOtpVerified = (completionToken: string) => {
    setForgotCompletionToken(completionToken);
    setResetFlow("password");
  };

  const onForgotSubmit = async (data: ForgotPasswordForm) => {
    if (!config?.authServiceUrl) {
      setError("Configuration not loaded. Please try again.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await executeForgotPassword(config.authServiceUrl, data);
      setSuccess(true);
      setSubmittedEmail(data.email);
    } catch (err: any) {
      setError(err.message || "An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const onResetSubmit = async (data: ResetPasswordForm) => {
    if (!config?.authServiceUrl || !operationId) {
      setError("Configuration not loaded. Please try again.");
      return;
    }

    if (data.newPassword !== data.confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await saveForgotPassword(config.authServiceUrl, {
        operationId,
        password: data.newPassword,
        completionToken: forgotCompletionToken || undefined,
      });
      setResetSuccess(true);
    } catch (err: any) {
      setError(err.message || "Failed to reset password. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };



  const handleBackToLogin = () => {
    navigate("/login");
  };

  if (checkingLink) {
    return (
      <Container fluid className="fullscreenFixed">
        <Row className="h-100">
          <AuthLeftPanel
            title="Verifying Link"
            subtitle="Please wait while we verify your reset link"
            centeredWordmarkSrc={loginWordmarkWhite}
          />
          <AuthContentCol className={s.rightPanel}>
            <PageTransition>
              <MobileLogo />
              <div className={s.verifyTokenPanel} role="status" aria-live="polite">
                <CircularProgress size={48} thickness={4} className={s.verifyTokenSpinner} />
                <h5 className={s.verifyTokenTitle}>Verifying reset link…</h5>
                <p className={s.verifyTokenMessage}>
                  Please wait while we verify your password reset link.
                </p>
              </div>
            </PageTransition>
          </AuthContentCol>
        </Row>
      </Container>
    );
  }

  if (operationId && !isValidLink) {
    return (
      <Container fluid className="fullscreenFixed">
        <Row className="h-100">
          <AuthLeftPanel
            title="Link Expired"
            subtitle="This password reset link is no longer valid"
            centeredWordmarkSrc={loginWordmarkWhite}
          />
          <AuthContentCol className={s.rightPanel}>
            <PageTransition>
              <MobileLogo />
              <div className="d-flex justify-content-center align-items-center h-100">
                <div style={{ maxWidth: "450px", width: "100%" }}>
                  <div className="text-center mb-4">
                    <div className={s.errorIcon}>
                      <i className="fa-solid fa-circle-xmark"></i>
                    </div>
                    <h2 className={s.errorTitle}>Invalid or Expired Link</h2>
                    <p className={s.errorText}>
                      This password reset link has expired or has already been used.
                      Please request a new password reset link.
                    </p>
                  </div>
                  <PrimaryButton
                    onClick={() => navigate("/forgot")}
                    className={`${s.blueButton} w-100 mb-3`}
                  >
                    Request New Link
                  </PrimaryButton>
                  <div className="text-center">
                    <Link to="/login" className={s.backToLoginLink}>
                      Back to Login
                    </Link>
                  </div>
                </div>
              </div>
            </PageTransition>
          </AuthContentCol>
        </Row>
      </Container>
    );
  }

  if (resetSuccess) {
    return (
      <Container fluid className="fullscreenFixed">
        <Row className="h-100">
          <AuthLeftPanel
            title="Password Reset"
            subtitle="Your password has been successfully updated"
            centeredWordmarkSrc={loginWordmarkWhite}
          />
          <AuthContentCol className={s.rightPanel}>
            <PageTransition>
              <MobileLogo />
              <div className="d-flex justify-content-center align-items-center h-100">
                <div style={{ maxWidth: "450px", width: "100%" }}>
                  <div className="text-center mb-4">
                    <div className={s.successIcon}>
                      <i className="fa-solid fa-circle-check"></i>
                    </div>
                    <h2 className={s.successTitle}>Password Reset Successfully!</h2>
                    <p className={s.successText}>
                      Your password has been updated. You can now log in with your new password.
                    </p>
                  </div>
                  <PrimaryButton
                    onClick={() => navigate("/login")}
                    className={`w-100`}
                  >
                    Go to Login
                  </PrimaryButton>
                </div>
              </div>
            </PageTransition>
          </AuthContentCol>
        </Row>
      </Container>
    );
  }

  if (operationId && isValidLink) {
    return (
      <Container fluid className="fullscreenFixed">
        <Row className="h-100">
          <AuthLeftPanel
            title="Create New Password"
            subtitle="Choose a strong password for your account"
            centeredWordmarkSrc={loginWordmarkWhite}
          />
          <AuthContentCol className={s.rightPanel}>
            <PageTransition>
              <MobileLogo />
              {resetFlow === "init" && error && !isLoading && (
                <Row className="justify-content-center mt-4">
                  <AuthFormCol>
                    <div className={s.errorMessage}>{error}</div>
                  </AuthFormCol>
                </Row>
              )}
              {resetFlow === "init" && (isLoading || !otpPrimed) && !error && (
                <LoadingSpinner
                  title="Preparing verification"
                  message="Please wait while we verify your session and prepare the next step."
                  className="h-100"
                />
              )}
              {resetFlow === "otp" && config?.authServiceUrl && forgotChallengeId && (
                <Row className="justify-content-center" style={{ marginTop: "15%" }}>
                  <AuthFormCol className={`text-lg-start`}>
                    <h2 className={s.title}>Verify it&apos;s you</h2>
                    <p className={s.subtitle}>
                      Use the one-time code we sent to continue resetting your password.
                    </p>
                    {error && <div className={s.errorMessage}>{error}</div>}
                    <AdminOtpPanel
                      authServiceUrl={config.authServiceUrl}
                      challengeId={forgotChallengeId}
                      smsEligible={forgotOtpSmsEligible}
                      initialChannel={forgotOtpDeliveryChannel}
                      onVerified={onForgotOtpVerified}
                    />
                  </AuthFormCol>
                </Row>
              )}
              {resetFlow === "password" && forgotCompletionToken && (
                <>
              <Row className="justify-content-center" style={{ marginTop: "15%" }}>
                <AuthFormCol className={`text-lg-start`}>
                  <h2 className={s.title}>Reset Your Password</h2>
                  <p className={s.subtitle}>
                    Enter a new password for your account.
                  </p>
                  {error && (
                    <div className={s.errorMessage}>
                      {error}
                    </div>
                  )}
                </AuthFormCol>
              </Row>
              <FormProvider {...resetMethods}>
                <Form onSubmit={resetMethods.handleSubmit(onResetSubmit)} noValidate>
                  <Row className="mt-4 justify-content-center">
                    <AuthFormCol>
                      <FormInput
                        name="newPassword"
                        type="password"
                        rules={passwordRules}
                        icon="fa-solid fa-lock"
                        placeholder="Enter new password"
                      />
                    </AuthFormCol>
                  </Row>
                  <Row className="mt-3 justify-content-center">
                    <AuthFormCol>
                      <FormInput
                        name="confirmPassword"
                        type="password"
                        rules={{
                          ...passwordRules,
                          validate: (value: string) => {
                            const password = resetMethods.getValues('newPassword');
                            return value === password || 'Passwords do not match';
                          }
                        }}
                        icon="fa-solid fa-lock"
                        placeholder="Confirm new password"
                      />
                    </AuthFormCol>
                  </Row>
                  <Row className="mt-4 justify-content-center">
                    <AuthFormCol>
                      <PrimaryButton
                        type="submit"
                        className={`w-100`}
                        loading={isLoading}
                        loadingText="Resetting..."
                        disabled={isLoading}
                      >
                        Reset Password
                      </PrimaryButton>
                    </AuthFormCol>
                  </Row>
                  <Row className="mt-3 justify-content-center">
                    <AuthFormCol className="text-center">
                      <Link to="/login" className={s.backToLoginLink}>
                        <i className="fa-solid fa-arrow-left me-2"></i>
                        Back to Login
                      </Link>
                    </AuthFormCol>
                  </Row>
                </Form>
              </FormProvider>
                </>
              )}
            </PageTransition>
          </AuthContentCol>
        </Row>
      </Container>
    );
  }

  if (success) {
    return (
      <Container fluid className="fullscreenFixed">
        <Row className="h-100">
          <AuthLeftPanel
            title="Check Your Email"
            subtitle="Password reset instructions sent"
            centeredWordmarkSrc={loginWordmarkWhite}
          />
          <AuthContentCol className={s.rightPanel}>
            <PageTransition>
              <MobileLogo />
              <div className="d-flex justify-content-center align-items-center h-100">
                <div style={{ maxWidth: "450px", width: "100%" }}>
                  <div className="text-center mb-4">
                    <div className={s.successIcon}>
                      <i className="fa-solid fa-envelope-circle-check"></i>
                    </div>
                    <h2 className={s.successTitle}>Check your inbox!</h2>
                    <p className={s.successText}>
                      We've sent password reset instructions to{" "}
                      <strong>{submittedEmail}</strong>
                    </p>
                    <p className={s.successSubText}>
                      Please check your email and follow the instructions to reset
                      your password. The link will expire in 24 hours.
                    </p>
                  </div>
                  <PrimaryButton
                    onClick={handleBackToLogin}
                    className={`${s.blueButton} w-100 mb-3`}
                  >
                    Back to Login
                  </PrimaryButton>
                  <div className="text-center">
                    <p className={s.helpText}>
                      Didn't receive the email? Check your spam folder or{" "}
                      <button
                        className={s.linkButton}
                        onClick={() => window.location.href = 'mailto:support@viewspark.com'}
                      >
                        contact support
                      </button>
                    </p>
                  </div>
                </div>
              </div>
            </PageTransition>
          </AuthContentCol>
        </Row>
      </Container>
    );
  }

  return (
    <Container fluid className="fullscreenFixed">
      <Row className="h-100">
        <AuthLeftPanel
          title="Reset Your Password"
          subtitle="We'll help you get back in"
          centeredWordmarkSrc={loginWordmarkWhite}
        />
        <AuthContentCol className={s.rightPanel}>
          <PageTransition>
            <MobileLogo />
            <Row className="justify-content-center" style={{ marginTop: "15%" }}>
              <AuthFormCol className={`text-lg-start`}>
                <h2 className={s.title}>Forgot Password?</h2>
                <p className={s.subtitle}>
                  Enter your email address and we'll send you instructions to
                  reset your password.
                </p>
                {error && (
                  <div className={s.errorMessage}>
                    {error}
                  </div>
                )}
              </AuthFormCol>
            </Row>
            <FormProvider {...forgotMethods}>
              <Form onSubmit={forgotMethods.handleSubmit(onForgotSubmit)} noValidate>
                <Row className="mt-4 justify-content-center">
                  <AuthFormCol>
                    <FormInput
                      name="email"
                      type="email"
                      rules={emailRules}
                      icon={"fa-regular fa-envelope"}
                      placeholder="Enter your email"
                    />
                  </AuthFormCol>
                </Row>
                <Row className="mt-4 justify-content-center">
                  <AuthFormCol>
                    <PrimaryButton
                      type="submit"
                      className={`${s.blueButton} w-100`}
                      loading={isLoading}
                      loadingText="Sending..."
                      disabled={isLoading}
                    >
                      Reset Password
                    </PrimaryButton>
                  </AuthFormCol>
                </Row>
                <Row className="mt-3 justify-content-center">
                  <AuthFormCol className="text-center">
                    <Link to="/login" className={s.backToLoginLink}>
                      <i className="fa-solid fa-arrow-left me-2"></i>
                      Back to Login
                    </Link>
                  </AuthFormCol>
                </Row>
              </Form>
            </FormProvider>
          </PageTransition>
        </AuthContentCol>
      </Row>
    </Container>
  );
};

export default withRouter(serviceCaller(withStyles(s)(ForgotPasswordPage)));
